import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../order.schema';

interface UseWeightAutoFillArgs {
  itemPath: ItemPath;
  /** Só o item raiz de produto com formato reconhecido tem o Peso dirigido pela massa. */
  enabled: boolean;
}

export interface WeightAutoFillState {
  /** massa por peça × peças, ou null quando falta uma das duas. */
  autoWeight: number | null;
  isWeightAuto: boolean;
  isQuantityAuto: boolean;
}

const round4 = (value: number) => Math.round(value * 10000) / 10000;

const toNum = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Peso (kg) = massa por peça × peças, com a mesma regra da Especificação: o cálculo escreve
 * sobre vazio ou sobre o último valor que ele mesmo escreveu; valor digitado vence; limpar o
 * campo reativa. Em KG a quantidade recebe o mesmo valor, com dono próprio.
 */
export const useWeightAutoFill = ({ itemPath, enabled }: UseWeightAutoFillArgs): WeightAutoFillState => {
  const { control, getValues, setValue } = useFormContext<OrderSchemaType>();
  const lastAutoWeightRef = useRef<number | null>(null);
  const lastAutoQuantityRef = useRef<number | null>(null);

  const massPerPiece = useWatch({ control, name: `${itemPath}.massPerPiece` as any });
  const quantityPieces = useWatch({ control, name: `${itemPath}.quantityPieces` as any });
  const unitName = useWatch({ control, name: `${itemPath}.unitOfMeasurementName` as any }) as string | undefined;
  const weight = useWatch({ control, name: `${itemPath}.weight` as any });
  const quantity = useWatch({ control, name: `${itemPath}.quantity` as any });
  const isKgUnit = String(unitName || '').toUpperCase() === 'KG';

  const mass = toNum(massPerPiece);
  const pieces = toNum(quantityPieces);
  const autoWeight = mass !== null && mass > 0 && pieces !== null && pieces > 0 ? round4(mass * pieces) : null;

  useEffect(() => {
    if (!enabled) return;
    const opts = { shouldDirty: true, shouldValidate: false };
    const weightPath = `${itemPath}.weight` as any;
    const quantityPath = `${itemPath}.quantity` as any;

    const currentWeight = toNum(getValues(weightPath));
    const weightOwnedByCalc = currentWeight === null || currentWeight === lastAutoWeightRef.current;
    if (autoWeight === null) {
      // Sem massa ou sem peças: o valor automático sai; o digitado fica.
      if (currentWeight !== null && currentWeight === lastAutoWeightRef.current) setValue(weightPath, undefined, opts);
      lastAutoWeightRef.current = null;
    } else if (weightOwnedByCalc) {
      if (currentWeight !== autoWeight) setValue(weightPath, autoWeight, opts);
      lastAutoWeightRef.current = autoWeight;
    }

    if (!isKgUnit) {
      lastAutoQuantityRef.current = null;
      return;
    }
    const currentQuantity = toNum(getValues(quantityPath));
    const quantityOwnedByCalc = currentQuantity === null || currentQuantity === lastAutoQuantityRef.current;
    if (autoWeight === null) {
      if (currentQuantity !== null && currentQuantity === lastAutoQuantityRef.current) setValue(quantityPath, undefined, opts);
      lastAutoQuantityRef.current = null;
    } else if (quantityOwnedByCalc) {
      if (currentQuantity !== autoWeight) setValue(quantityPath, autoWeight, opts);
      lastAutoQuantityRef.current = autoWeight;
    }
  }, [enabled, autoWeight, isKgUnit, weight, quantity, itemPath, getValues, setValue]);

  const weightNow = toNum(weight);
  const quantityNow = toNum(quantity);
  return {
    autoWeight,
    isWeightAuto: enabled && autoWeight !== null && weightNow !== null && weightNow === lastAutoWeightRef.current,
    isQuantityAuto: enabled && isKgUnit && autoWeight !== null && quantityNow !== null && quantityNow === lastAutoQuantityRef.current,
  };
};
